import React, { useMemo } from 'react';
import { seeded } from './modes';

const BASE = { snow: 28, confetti: 26, petals: 18, bubbles: 16, sparks: 18, glyph: 14 };
const SCALE = { subtle: 0.5, normal: 1, festive: 1.7 };
const rangeOf = (rnd, a, b) => a + rnd() * (b - a);

const durationRange = (kind, motion) => {
  if (motion === 'drift') return [14, 24];
  return { snow: [9, 18], confetti: [6, 11], petals: [8, 14], bubbles: [7, 13], sparks: [3, 6], glyph: [8, 14] }[kind] || [8, 14];
};

const buildParticles = (p, intensity, isMobile, preview, seed) => {
  const rnd = seeded(seed);
  const motion = p.motion || (p.kind === 'bubbles' || p.kind === 'sparks' ? 'rise' : 'fall');
  let n = Math.round((BASE[p.kind] || 14) * (SCALE[intensity] || 1) * (p.boost || 1) * (isMobile ? 0.6 : 1));
  if (motion === 'drift') n = Math.max(2, Math.round(n / 2.5));
  if (preview) n = Math.min(n, 14);
  const [dMin, dMax] = durationRange(p.kind, motion);
  return Array.from({ length: n }, (_, i) => {
    const dur = rangeOf(rnd, dMin, dMax);
    return {
      id: i,
      left: rangeOf(rnd, 0, 100),
      y: rangeOf(rnd, 6, 42),
      size: rangeOf(rnd, 0.55, 1.15) * (p.size || 1),
      color: p.colors ? p.colors[Math.floor(rnd() * p.colors.length)] : undefined,
      glyph: p.glyphs ? p.glyphs[Math.floor(rnd() * p.glyphs.length)] : undefined,
      dur,
      delay: -rangeOf(rnd, 0, dur),
      sway: rangeOf(rnd, 2.2, 4.4),
      opacity: rangeOf(rnd, 0.6, 1),
      motion,
    };
  });
};

const Shape = ({ kind, q, isMobile }) => {
  const m = isMobile ? 0.8 : 1;
  switch (kind) {
    case 'snow': { const s = 5 + q.size * 5 * m; return <span className="szn-dot" style={{ width: s, height: s, opacity: q.opacity, filter: `blur(${s > 8 ? 0.6 : 0}px)` }} />; }
    case 'confetti': return <span className="szn-rect" style={{ width: 6 + q.size * 3, height: 10 + q.size * 5, background: q.color, opacity: q.opacity }} />;
    case 'petals': return <span className="szn-petal" style={{ width: 8 + q.size * 5, height: 12 + q.size * 6, background: q.color, opacity: q.opacity }} />;
    case 'bubbles': { const s = 8 + q.size * 14 * m; return <span className="szn-bubble" style={{ width: s, height: s, opacity: q.opacity }} />; }
    case 'sparks': { const s = 3 + q.size * 3; return <span className="szn-spark" style={{ width: s, height: s, opacity: q.opacity }} />; }
    default: return <span className="szn-emoji" style={{ fontSize: (14 + q.size * 10) * m, opacity: q.opacity, filter: 'none' }}>{q.glyph}</span>;
  }
};

const Fireworks = ({ colors, isMobile, preview, seed }) => {
  const bursts = useMemo(() => {
    const rnd = seeded(seed + 99);
    const n = preview ? 3 : isMobile ? 3 : 5;
    return Array.from({ length: n }, (_, i) => ({
      id: i,
      top: rangeOf(rnd, 6, 38),
      left: rangeOf(rnd, 8, 88),
      size: isMobile ? rangeOf(rnd, 60, 110) : rangeOf(rnd, 90, 170),
      color: colors[i % colors.length],
      dur: rangeOf(rnd, 2.6, 3.6),
      delay: rangeOf(rnd, 0, 3),
    }));
  }, [colors, isMobile, preview, seed]);
  return bursts.map((b) => (
    <React.Fragment key={b.id}>
      <span className="szn-burst" style={{ top: `${b.top}%`, left: `${b.left}%`, width: b.size, height: b.size, marginLeft: -b.size / 2, '--c': b.color, '--dur': `${b.dur}s`, '--delay': `${b.delay}s` }} />
      <span className="szn-burst" style={{ top: `${b.top}%`, left: `${b.left}%`, width: b.size * 0.55, height: b.size * 0.55, marginLeft: -b.size * 0.275, '--c': '#ffffff', '--dur': `${b.dur}s`, '--delay': `${b.delay + 0.25}s` }} />
    </React.Fragment>
  ));
};

export const Particles = ({ particles, intensity, isMobile, preview, seed = 1 }) => {
  const items = useMemo(() => (particles ? buildParticles(particles, intensity, isMobile, preview, seed) : []), [particles, intensity, isMobile, preview, seed]);
  if (!particles) return null;
  const fwColors = particles.colors || ['#f5c842', '#60a5fa', '#f87171'];
  return (
    <div className="absolute inset-0" data-testid={`szn-particles-${particles.kind}`}>
      {items.map((q) => (
        <span
          key={q.id}
          className={`szn-p ${q.motion === 'rise' ? 'szn-rise' : ''} ${q.motion === 'drift' ? 'szn-drift' : ''}`}
          style={{ left: q.motion === 'drift' ? undefined : `${q.left}%`, '--y': `${q.y}%`, '--dur': `${q.dur}s`, '--delay': `${q.delay}s`, '--sway': `${q.sway}s` }}
        >
          <Shape kind={particles.kind} q={q} isMobile={isMobile} />
        </span>
      ))}
      {particles.fireworks && <Fireworks colors={fwColors} isMobile={isMobile} preview={preview} seed={seed} />}
    </div>
  );
};
